import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { useDispatch, useSelector } from "react-redux";
import { actionTypeOptions, transactionStatusOptions } from "../../../../constants/constantList";
import { cloneDeep, debounce } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { validateWorkflowItemName } from "../../../../utils/utilFunctions";
import ErrorModal from "../../Forms/FieldDataValidation/ErrorModal";

function ActionProperties() {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const dispatch = useDispatch();
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const currentTab = rightTree.currentTab;
  const { translatedData } = rightTree.tree.body;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const wfItemId = selectedNode?.ID;

  const [name, setName] = useState("");
  const [donotLogWfItemDetails, setDonotLogWfItemDetails] = useState(false);
  const [disableActionItem, setDisableActionItem] = useState(false);
  const [action, setAction] = useState(t('SELECT'));
  const [wfItem, setWfItem] = useState(t('SELECT'));
  const [wfItems, setWfItems] = useState([])
  const [txnStatus, setTxnStatus] = useState('SUCCESS');
  const [clearAllErrors, setClearAllErrors] = useState(false);
  const [error, setError] = useState("");

  // Name validation states
  const [nameErrorModal, setNameErrorModal] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  const actionDropdownOptions = [{text:t('SELECT'), value: t('SELECT')}, ...actionTypeOptions]?.map((item, idx) => (
    <MenuItem value={t(item.value)} key={`${item.value}-${idx}`}> {t(item.text)}</MenuItem>
  ));

  const wfItemDropdownOptions = wfItems?.map((item, idx) => (
    <MenuItem value={item.title} key={`${item}-${idx}`}>{item.title}</MenuItem>
  ))
  const txnStatusDropdownOptions = transactionStatusOptions?.map((item, idx) => (
    <MenuItem value={item.value} key={`${item.text}-${idx}`}>{t(item.text)}</MenuItem>
  ))

  const handleActionNameChange = (e) => {
    const { value } = e.target;
    setName(value);
    if (parentScreen !== "workflow") {
      if(value !== '') debounceUpdateNode('NAME', value);
    }
  };

  const handleActionNameBlur = (eVal, e) => {
    if (parentScreen === "workflow") {
      const oldName = selectedNode?.NAME || '';
      
      const validationResult = validateWorkflowItemName(
        eVal, 
        oldName, 
        translatedData, 
        selectedNode?.key, 
        'ACTION'
      );
      
      if (validationResult.isValid) {
        const newName = eVal?.trim() || '';
        setName(newName);
        if (newName !== oldName) {
          // Mark template as changed immediately to ensure flag is set before tab switch
          dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
          updateNode('NAME', newName);
        }
      } else {
        if (validationResult.errorMessage) {
          setNameErrorMessage(t(validationResult.errorMessage));
          setNameErrorModal(true);
        } else if (validationResult.shouldRevert) {
          setName(oldName);
        }
      }
    } else {
      let newName = eVal;
      if(eVal === '' || eVal === undefined || eVal === null){
        newName = selectedNode?.NAME;
      }
      if (newName !== selectedNode?.NAME) {
        setName(newName);
        debounceUpdateNode('NAME', newName);
      }
    }
  };

  const handleNameErrorModal = (isOpen) => {
    setNameErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setName(oldName);
    }
  };

  const handleChange = (checked, setState, key) => {
    setState(checked);
    const isInvertedKey = key === 'LOG_WFITEM_DETAILS' || key === 'ENABLED';
    const booleanToString = isInvertedKey ? (checked ? 'FALSE' : 'TRUE') : (checked ? 'TRUE' : 'FALSE');

    updateNode(key, booleanToString)
  };
  
  const handleActionChange = (e) => {
    const { value } = e.target;
    setAction(value);
    setWfItem(t('SELECT'));
    setTxnStatus('SUCCESS');
    setClearAllErrors(false);
    
    if(value !== t('SELECT')) debounceUpdateNode("ACTION_TYPE", value);
  }

  const handleWfItemChange = (e) => {
    const { value } = e.target;
    setWfItem(value);
  
    if (value !== t('SELECT')) {
      const selectedItem = wfItems.find(
        item => (item?.NAME || item.title) === value
      );
  
      if (selectedItem) { 
        debounceUpdateNode(null, null, {'SOURCE_WFITEM_NAME': value, 'SOURCE_WFITEM': selectedItem.ID }, null)
      }
    }
  };
  
  const handleSetTxnStatus = (e) => {
    setTxnStatus(e.target.value)
    debounceUpdateNode("NEW_TXN_STATUS", e.target.value)
  }
  
  const updateNode = (key, value, bulkAdd, bulkRemove) => {
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;
  
      switch (key) {
        case 'NAME':
          obj.title = value;
          break;
  
        case 'ACTION_TYPE':
          if (value === 'Set Transaction Status') {
            obj.NEW_TXN_STATUS = 'SUCCESS';
          } else {
            delete obj.NEW_TXN_STATUS;
          }
  
          if (value === 'Stop All Workflow Processing') {
            obj.ICON_DISPLAY = 'STOP_ACTION';
            obj.CLEAR_ALL_ERRORS = 'FALSE';
            if(obj.hasOwnProperty('NEW_TXN_STATUS')) delete obj.NEW_TXN_STATUS;
          } else {
            obj.ICON_DISPLAY = 'ACTION'
          }
          break;
      }
      
      if(bulkAdd){
        for(let key in bulkAdd){
          obj[key] = bulkAdd[key] ;
        }
      }
      if(!bulkAdd || !bulkAdd.hasOwnProperty(key)) obj[key] = value;
      dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: rightTree.currentTab,
            data: obj,
          })
        );
    });
  
    dispatch(treeActions.setNewRightTreeTranslatedData({
      screen: parentScreen,
      type: rightTree.currentTab,
      data: updatedData,
    }));
  };
  
  const debounceUpdateNode = useCallback(debounce(updateNode, 200),[
    rightTree.tree
  ]) 

  const getWorkflowItems = (action) => {
    if ([t('ACTION_CLOSE_ALL'), t('ACTION_STOP_WORKFLOW_PROCESSING')].includes(action)) return [];
  
    const matchingIcons = action === t('ACTION_DELETE_FILE')
      ? ['GET_DATA']
      : ['GET_DATA', 'SEND_DATA'];
  
    let foundSelected = false;
    const results = [];
    results.push({title:t('SELECT')})

    const traverse = (nodes) => {
      for (const node of nodes) {
       
        if (foundSelected) return; // Stop traversal once selected node is found
        
        if (node.key === selectedNode?.key) {
          foundSelected = true;
          return;
        }
  
        if (matchingIcons.includes(node.ICON_DISPLAY)) {
          results.push(node);
        }
  
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
  
    traverse(translatedData);
    return results;
  };
    
  useEffect(() => {
    setName(selectedNode?.NAME || "");
    setDonotLogWfItemDetails(selectedNode?.LOG_WFITEM_DETAILS === "TRUE" ? false : true);
    setDisableActionItem(selectedNode?.ENABLED === "TRUE" ? false : true);
    setAction(selectedNode?.ACTION_TYPE || t('SELECT'));
    // setWfItem(wfItems.includes(selectedNode?.SOURCE_WFITEM_NAME) ? selectedNode?.SOURCE_WFITEM_NAME : t('SELECT'))
    setTxnStatus(selectedNode?.NEW_TXN_STATUS || 'SUCCESS')
    setClearAllErrors(selectedNode?.CLEAR_ALL_ERRORS === 'TRUE');

  }, [selectedNode?.key]);

  const formatModalContent = (string) => {
    let splitText = string.split(`\\n`);
    return (
      <>
        {splitText.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            <br />
            {idx === 0 && <br />}
          </React.Fragment>
        ))}
      </>
    );
  };

  useEffect(() => {
    if(action === "CloseAllCachedConnections" ) {
      const isAboveGetDataSendDataOrPluginRunConcurrently = checkIfAboveGetDataSendDataOrPluginRunConcurrently(translatedData, selectedNode);
      if(isAboveGetDataSendDataOrPluginRunConcurrently) {
        setError(formatModalContent(t("LBL_ACTIONITEM_CONCURRENT_WARN_EXISTING_ITEM")))
      }
    }
  }, [selectedNode?.key, action]);

  useEffect(() => {
    if (action) {
      const workflowItems = getWorkflowItems(action); 
      setWfItems(workflowItems);
  
      const match = workflowItems.find(
        item => item?.title === selectedNode?.SOURCE_WFITEM_NAME
      );
  
      setWfItem(match ? selectedNode?.SOURCE_WFITEM_NAME : t('SELECT'));
    }
  }, [action]);
  
 
  return (
    <Stack spacing={"15px"} className="tp-segment">
      <FormControl variant="outlined" className="width-100" size="small">
        <InputLabel
          htmlFor="action-name-input"
          sx={{ padding: "0 6px !important", left: "-5px" }}>
          {t("COL_NAME")}
        </InputLabel>
        <OutlinedInput
          id="action-name-input"
          size="small"
          variant="outlined"
          fullWidth
          autoComplete="off"
          value={name}
          endAdornment={
            <div style={styles.getDataNameAdornment}>{`ID: ${wfItemId}`}</div>
          }
          sx={{ justifyContent: "flex-end" }}
          onChange={handleActionNameChange}
          onBlur={(e) => handleActionNameBlur(e.target.value, e)}
        />
      </FormControl>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <FormControlLabel
          label={t("LBL_LOG_NO_WFITEM_DETAILS")}
          sx={{ height: "30px" }}
          className={"width-fit-content"}
          control={
            <Checkbox
              checked={donotLogWfItemDetails}
              size="small"
              name="donotLogWfItemDetails"
              onChange={(e, checked) =>
                handleChange(
                  checked,
                  setDonotLogWfItemDetails,
                  "LOG_WFITEM_DETAILS"
                )
              }
            />
          }
        />
        <FormControlLabel
          label={t("DISABLE_ACT")}
          sx={{ height: "30px" }}
          className={"width-fit-content"}
          control={
            <Checkbox
              checked={disableActionItem}
              size="small"
              name="disableWfItem"
              onChange={(e, checked) => {
                handleChange(checked, setDisableActionItem, "ENABLED");
              }}
            />
          }
        />
      </div>
      <FormControl>
        <InputLabel id="action-options">{t("ACTION")}</InputLabel>
        <Select
          size="small"
          id="action-options"
          value={action}
          label={t("ACTION")}
          onChange={handleActionChange}>
          {actionDropdownOptions}
        </Select>
      </FormControl>
      {[t('ACTION_ROLLBACK'),t('ACTION_COMMIT'),t('ACTION_DELETE_FILE')].includes(action) ? (
        <FormControl>
          <InputLabel id="workflow-item-options">
            {t("LBL_WORKFLOW_ITEM")}
          </InputLabel>
          <Select
            size="small"
            id="workflow-item-options"
            value={wfItem}
            label={t("LBL_WORKFLOW_ITEM")}
            onChange={handleWfItemChange}>
            {wfItemDropdownOptions}
          </Select>
        </FormControl>
      ) : action === t('ACTION_SET_TXN_STATUS') ? (
        <>
          <FormControl>
            <InputLabel id="set-txn-options">
              {t("ACTION_SET_TXN_STATUS")}
            </InputLabel>
            <Select
              size="small"
              id="set-txn-options"
              value={txnStatus}
              label={t("ACTION_SET_TXN_STATUS")}
              onChange={handleSetTxnStatus}>
              {txnStatusDropdownOptions}
            </Select>
          </FormControl>
          <FormControlLabel
            label={t("BTN_CLEAR_ALL_ERRORS")}
            sx={{ height: "30px" }}
            className={"width-fit-content"}
            control={
              <Checkbox
                checked={clearAllErrors}
                size="small"
                name="clearAllErrors"
                onChange={(e, checked) => {
                  handleChange(checked, setClearAllErrors, "CLEAR_ALL_ERRORS");
                }}
              />
            }
          />
          
        </>

      ): ''}
      {error && <div style={{ color: "grey", fontSize: "12px", marginTop: "5px" }}>{error}</div>}

      {/* Error Modal for name validation - only for workflow screens */}
      {parentScreen === "workflow" && (
        <ErrorModal
          errorModal={nameErrorModal}
          setErrorModal={setNameErrorModal}
          errorMessage={nameErrorMessage}
          errorType={t('ERROR')}
          handleErrorModal={handleNameErrorModal}
        />
      )}
    </Stack>
  );
}

const checkIfAboveGetDataSendDataOrPluginRunConcurrently = (translatedData, selectedNode) => {
  let found = false;
  let result = false;

  function traverse(nodes, ancestorsConcurrent) {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (found) return;
      // If this is the selected node, stop traversal
      if (node.key === selectedNode.key) {
        found = true;
        result = !!ancestorsConcurrent;
        return;
      }
      // Only check CONCURRENT if ICON_DISPLAY is GET_DATA, SEND_DATA, or CALL_PLUGIN
      let isRelevantIcon = node.ICON_DISPLAY === "GET_DATA" || node.ICON_DISPLAY === "SEND_DATA" || node.ICON_DISPLAY === "CALL_PLUGIN";
      let nextAncestorsConcurrent = ancestorsConcurrent;
      if (isRelevantIcon && node.CONCURRENT === "TRUE") {
        // If any relevant ancestor has CONCURRENT = "TRUE", return true immediately
        found = true;
        result = true;
        return;
      }
      if (node.children && node.children.length > 0) {
        traverse(node.children, nextAncestorsConcurrent || (isRelevantIcon && node.CONCURRENT === "TRUE"));
      }
    }
  }

  traverse(translatedData, false);
  return result;
}

export default ActionProperties;

const styles = {
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
